import type { FilterField, GroupedSelectFilterField } from '@ooh/ui/components/filter-panel';
import { bucketGroupedFilterValues, namespacedKey } from '@ooh/ui/lib/grouped-filter';
import type {
  FilterOptionsResponse,
  MediaUnitFilters,
  ExcludableFilterField,
} from '@ooh/contracts';
import { MEDIA_FORMAT_LABELS } from '@ooh/contracts';
import { SHARED_FILTER_FIELD_IDS } from '../../../lib/filter-config';
import { buildRegionOptions as buildRegionOptionsShared } from '../../../lib/region-options';
import { buildMediaTypeOptions as buildMediaTypeOptionsShared } from '../../../lib/media-type-options';
import { capitalize } from './formatters';
import { RANGE_FIELD_DEFS } from '../../../lib/range-field-defs';
import {
  CATALOG_ONLY_REAL_ARRAY_FIELD_IDS,
  CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS,
  buildCatalogOnlyFields,
  buildSupplierIdentityFields,
} from './filter-fields-catalog-only';

export { RANGE_FIELD_DEFS, type RangeFieldDef } from '../../../lib/range-field-defs';

/**
 * Single source of truth for the location-dimension group definitions merged
 * into the combined "Lokalizacja" pill. `LOCATION_GROUP_FIELD_IDS` and
 * `buildLocationFilterField`'s `groups` array are both derived from this array
 * so the two can never diverge.
 */
const LOCATION_GROUPS: ReadonlyArray<{
  id: string;
  headingLabelKey: 'region' | 'county' | 'agglomeration' | 'city' | 'administrativeArea';
  buildOptions: (opts: FilterOptionsResponse) => Array<{ value: string; label: string }>;
  /**
   * Omits this group from the combined field when its option list is empty.
   * Unlike region/county/agglomeration/city (which always render, even with
   * zero options), the administrative-area dataset is optional/rollout-gated
   * (Warsaw-only initially) — see spec scenario "Empty administrative-area
   * options hide only that group".
   */
  hideWhenEmpty?: boolean;
}> = [
  {
    id: 'region',
    headingLabelKey: 'region',
    buildOptions: (opts) => buildRegionOptions(opts.regions),
  },
  {
    id: 'county',
    headingLabelKey: 'county',
    buildOptions: (opts) => opts.counties.map((c) => ({ value: c, label: c })),
  },
  {
    id: 'agglomeration',
    headingLabelKey: 'agglomeration',
    buildOptions: (opts) => (opts.agglomerations ?? []).map((a) => ({ value: a, label: a })),
  },
  {
    id: 'city',
    headingLabelKey: 'city',
    buildOptions: (opts) => opts.cities.map((c) => ({ value: c, label: c })),
  },
  {
    id: 'administrativeAreaIds',
    headingLabelKey: 'administrativeArea',
    buildOptions: (opts) =>
      (opts.administrativeAreas ?? []).map((a) => ({ value: a.id, label: a.name })),
    hideWhenEmpty: true,
  },
];

/** Field ids of the location dimensions merged into the combined "Lokalizacja" pill. */
export const LOCATION_GROUP_FIELD_IDS = LOCATION_GROUPS.map((g) => g.id);

// URL param keys that correspond to filter fields in the router search state.
// Derived from SHARED_FILTER_FIELD_IDS — the single source of truth.
// panelValueToFilters iterates this array so each value is forwarded to the API.
export const FILTER_FIELD_IDS = SHARED_FILTER_FIELD_IDS;

export type FilterFieldId = (typeof FILTER_FIELD_IDS)[number];

export interface FilterFieldLabels {
  region: string;
  county: string;
  agglomeration: string;
  city: string;
  formatCode: string;
  mediaType: string;
  supplierId: string;
  lighting: string;
  supplierNumber: string;
  grwId: string;
  searchPlaceholder: string;
  /** Localized labels for each MediaTypeEnum value, keyed by enum value. */
  mediaTypeOptionLabels: Record<string, string>;
  /** Combined "Lokalizacja" field labels — consumed by `buildLocationFilterField`. */
  location: string;
  locationSearchPlaceholder: string;
  locationInfoHint: string;
  locationInfoHintLabel: string;
  /** Heading for the administrative-area group inside the combined "Lokalizacja" pill ("Dzielnica"). */
  administrativeArea: string;
  // ─── New catalog filter labels (CU-86cataqt9) ───
  material: string;
  multiformat: string;
  tags: string;
  address: string;
  area: string;
  width: string;
  height: string;
  audience: string;
  catalogPrice: string;
  rent: string;
  // ─── Remaining displayed cost-column range filters ───
  exposureCost: string;
  additionalCosts: string;
  discountedPrice: string;
  lastMinutePrice: string;
  starterPrice: string;
  productionCost: string;
  installationCost: string;
  dismantlingCost: string;
  clientProduction: string;
  clientInstallation: string;
  clientDismantling: string;
  clientAdditionalFees: string;
  // ─── Column-parity filter labels (catalog-column-filter-parity) ───
  notes: string;
  roadNumber: string;
}

/**
 * Catalog-only FilterPanel field ids whose URL persistence reuses the same
 * generic array-of-strings storage as `useSharedFilters` (material/multiformat/tags
 * are real `select` values; `address`/`notes`/`roadNumber` are `text` fields
 * whose FilterPanel value is a single-element array `[typedString]` — same
 * wire shape). Derived from the two disjoint sub-lists (not hand-duplicated)
 * so a future addition to either sub-list in filter-fields-catalog-only.ts
 * can never drift out of sync with this URL-persistence list — see
 * `use-media-units-page.ts`'s `useSharedFilters(CATALOG_ONLY_ARRAY_FIELD_IDS)`
 * and `validate-search.ts`.
 */
export const CATALOG_ONLY_ARRAY_FIELD_IDS = [
  ...CATALOG_ONLY_REAL_ARRAY_FIELD_IDS,
  ...CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS,
] as const;

/**
 * Sidecar filter params handled outside the array-only FilterPanel value model
 * (modeled on the near-POI sidecar). These ride the URL as scalar/array params and
 * are forwarded into MediaUnitFilters by panelValueToFilters.
 */
export interface SidecarFilterParams {
  /** Multi-column free-text search term. Blank/whitespace is treated as absent. */
  search?: string;
  /** "bez zdjęcia": restrict to units with no photos. */
  noPhoto?: boolean;
  /** Availability window start (YYYY-MM-DD). Only forwarded with availabilityTo. */
  availabilityFrom?: string;
  /** Availability window end (YYYY-MM-DD). Only forwarded with availabilityFrom. */
  availabilityTo?: string;
  /** Dimension fields whose value arrays are negated (exclude mode). */
  exclude?: ExcludableFilterField[];
  /** Restrict the catalog to media units created/updated by a single import job. */
  importJobId?: number;
}

// ─── Normalization helpers ────────────────────────────────────────────────────

/**
 * Maps raw API region strings to canonical display options, capitalizing
 * unrecognized fallback values. See `src/lib/region-options.ts` for the full
 * shared normalization behavior (also used by the offer units-tab).
 */
export function buildRegionOptions(regions: string[]): Array<{ value: string; label: string }> {
  return buildRegionOptionsShared(regions, capitalize);
}

/**
 * Maps raw API format entries to canonical display options using MEDIA_FORMAT_LABELS.
 *
 * - Recognized codes → canonical Polish label from MEDIA_FORMAT_LABELS.
 * - Unrecognized codes (raw import values) → DB label as fallback.
 * - Result is sorted by label using Polish locale.
 */
export function buildFormatOptions(
  formats: Array<{ code: string; label: string }>,
): Array<{ value: string; label: string }> {
  return formats
    .map((f) => ({
      value: f.code,
      label: MEDIA_FORMAT_LABELS[f.code] ?? f.label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
}

/**
 * Maps MediaTypeEnum values from the filter-options API to display options.
 * Labels come from the caller-provided localized map (kept translation-free here).
 * Unmapped values fall back to a capitalized form of the raw enum value.
 *
 * Re-exported for backward compatibility — the implementation now lives in the
 * neutral `src/lib/media-type-options.ts` so the offer units-tab
 * (`features/offers/units-tab/lib/filter-fields.ts`) can share it without a
 * cross-feature import (WEB_ARCHITECTURE.md:36).
 */
export function buildMediaTypeOptions(
  mediaTypes: FilterOptionsResponse['mediaTypes'],
  optionLabels: Record<string, string>,
): Array<{ value: string; label: string }> {
  return buildMediaTypeOptionsShared(mediaTypes, optionLabels);
}

/**
 * Builds the combined "Lokalizacja" grouped field — region + county +
 * agglomeration + city merged into ONE `groupedSelect` field with OR semantics
 * within the group (client-approved design; see the mockup at
 * apps/mockups/src/screens/media-inventory.mockup.tsx and the extracted
 * `GroupedFilterPill` primitive at packages/ui/src/components/grouped-filter-pill.tsx).
 *
 * Group `id`s match the real API field names in `sharedMediaUnitFilterFields`
 * (@ooh/contracts/schemas/media-unit-filter-fields), so the panel value/onChange
 * adapters below (`buildLocationPanelValue`, `adaptLocationFilterChange`) can
 * bucket a flat selection straight onto `MediaUnitFilters` via
 * `bucketGroupedFilterValues` (@ooh/ui/lib/grouped-filter).
 */
export function buildLocationFilterField(
  opts: FilterOptionsResponse | undefined,
  labels: FilterFieldLabels,
): GroupedSelectFilterField | null {
  if (!opts || !Array.isArray(opts.regions)) return null;
  return {
    type: 'groupedSelect',
    id: 'location',
    label: labels.location,
    pinned: true,
    searchPlaceholder: labels.locationSearchPlaceholder,
    infoHint: labels.locationInfoHint,
    infoHintLabel: labels.locationInfoHintLabel,
    groups: LOCATION_GROUPS.flatMap((g) => {
      const options = g.buildOptions(opts);
      if (g.hideWhenEmpty && options.length === 0) return [];
      return [{ id: g.id, heading: labels[g.headingLabelKey], options }];
    }),
  };
}

export interface BuildFilterFieldsOptions {
  /** Omits catalogPrice/rent range fields when false (cost.view gating). Default true. */
  canViewCosts?: boolean;
  /**
   * Omits the rent range field when false, on top of (not instead of)
   * canViewCosts — rent's own wildcard cost.view_rent gate is narrower
   * (field-visibility, CU-86cbgnt3z). Default true.
   */
  canViewRent?: boolean;
  /**
   * Omits supplierId/supplierNumber filter fields when false (supplier.view
   * gating, catalog-column-filter-parity) — the field is ABSENT from "Dodaj
   * filtr", never disabled. Default true.
   */
  canViewSupplierDetails?: boolean;
  /**
   * Appends the catalog-only fields (material/multiformat/tags/address/notes/
   * roadNumber + the numeric ranges) when true. Default false, but every real
   * caller now passes `true` explicitly — both `/media-units`
   * (`use-media-units-page.ts`) and `/map` (`use-map-pins-data.ts`), since
   * these dimensions are fully wired end-to-end into `MapPinsQuerySchema` too
   * (map-catalog-filter-parity) and are no longer /media-units-only. The
   * default stays `false` so a future caller must opt in deliberately instead
   * of silently inheriting every filter dimension.
   */
  includeCatalogOnlyFields?: boolean;
}

/** Builds the fields shared by every caller (catalog + /map) — always present. */
function buildStandardFields(
  opts: FilterOptionsResponse,
  labels: FilterFieldLabels,
  canViewSupplierDetails: boolean,
): FilterField[] {
  const locationField = buildLocationFilterField(opts, labels);
  const { supplierIdField, supplierNumberField } = buildSupplierIdentityFields(
    opts,
    labels,
    canViewSupplierDetails,
  );
  return [
    {
      type: 'select',
      id: 'mediaType',
      label: labels.mediaType,
      options: buildMediaTypeOptions(opts.mediaTypes ?? [], labels.mediaTypeOptionLabels),
      pinned: true,
    },
    ...(locationField ? [locationField] : []),
    {
      type: 'select',
      id: 'formatCode',
      label: labels.formatCode,
      options: buildFormatOptions(opts.formats),
    },
    ...supplierIdField,
    {
      type: 'select',
      id: 'lighting',
      label: labels.lighting,
      options: opts.lightingValues.map((l) => ({ value: l, label: l })),
    },
    ...supplierNumberField,
    {
      type: 'text',
      id: 'grwId',
      label: labels.grwId,
      placeholder: labels.searchPlaceholder,
    },
  ];
}

/** Resolves `BuildFilterFieldsOptions` gating flags with their defaults, in one place. */
function resolveFilterFieldsOptions(options: BuildFilterFieldsOptions | undefined): {
  canViewCosts: boolean;
  canViewRent: boolean;
  canViewSupplierDetails: boolean;
  includeCatalogOnlyFields: boolean;
} {
  return {
    canViewCosts: options?.canViewCosts ?? true,
    canViewRent: options?.canViewRent ?? true,
    canViewSupplierDetails: options?.canViewSupplierDetails ?? true,
    includeCatalogOnlyFields: options?.includeCatalogOnlyFields ?? false,
  };
}

export function buildFilterFields(
  opts: FilterOptionsResponse | undefined,
  labels: FilterFieldLabels,
  options?: BuildFilterFieldsOptions,
): FilterField[] {
  if (!opts || !Array.isArray(opts.regions)) return [];
  const { canViewCosts, canViewRent, canViewSupplierDetails, includeCatalogOnlyFields } =
    resolveFilterFieldsOptions(options);
  return [
    ...buildStandardFields(opts, labels, canViewSupplierDetails),
    // ─── New catalog-only filters (CU-86cataqt9) — /media-units only, see BuildFilterFieldsOptions ───
    ...(includeCatalogOnlyFields
      ? buildCatalogOnlyFields(opts, labels, canViewCosts, canViewRent)
      : []),
  ];
}

// ─── Combined "Lokalizacja" pill value/onChange adapter ───────────────────────
//
// FilterPanel's `value` model is one flat `string[]` per field id. The combined
// "location" groupedSelect field needs its OWN flat array (merged from the
// underlying region/county/agglomeration/city/administrativeAreaIds keys) for
// display, and any change to it must be redistributed back into those keys
// before reaching the URL state managed by `useSharedFilters` (which only
// knows about the real field ids, not "location").

/**
 * Merges the location dimension values into ONE flat array for the combined
 * pill's value, namespacing each entry as `${id}::${value}` to match
 * `GroupedFilterPill`'s namespaced selection contract (see grouped-filter-pill.tsx) —
 * identical raw values across groups (e.g. county "Kraków" / city "Kraków") would
 * otherwise collide in the flat array.
 */
export function buildLocationPanelValue(filterValue: Record<string, string[]>): string[] {
  return LOCATION_GROUP_FIELD_IDS.flatMap((id) =>
    (filterValue[id] ?? []).map((value) => namespacedKey(id, value)),
  );
}

/**
 * Wraps a FilterPanel `onChange` handler so a flat value update on the combined
 * "location" field is bucketed back into the underlying region/county/
 * agglomeration/city keys via `bucketGroupedFilterValues` before being forwarded.
 * Non-location updates pass through untouched.
 */
export function adaptLocationFilterChange(
  locationField: GroupedSelectFilterField | null,
  onChange: (value: Record<string, string[]>) => void,
): (value: Record<string, string[]>) => void {
  return (value) => {
    if (!locationField || !('location' in value)) {
      onChange(value);
      return;
    }
    const { location, ...rest } = value;
    onChange({ ...rest, ...bucketGroupedFilterValues(locationField.groups, location ?? []) });
  };
}

/** Decomposes each `range` field's `value[id] = [min, max]` pair into scalar filter keys. */
function applyRangeFilters(filters: MediaUnitFilters, value: Record<string, string[]>): void {
  for (const def of RANGE_FIELD_DEFS) {
    const pair = value[def.id];
    const min = pair?.[0]?.trim();
    const max = pair?.[1]?.trim();
    if (min) (filters as Record<string, number>)[def.minKey] = Number(min);
    if (max) (filters as Record<string, number>)[def.maxKey] = Number(max);
  }
}

/**
 * Decomposes a FilterPanel `value` object into the shared + catalog-only
 * filter dimensions (array fields, scalar-text fields, and numeric ranges) —
 * the part of `panelValueToFilters`'s job that `/map`'s own `combinedFilters`
 * (`use-map-pins-data.ts`) also needs, since /map has no near-POI/sidecar
 * params of its own to merge in here (it builds those separately). Extracted
 * so both callers decompose a FilterPanel value identically instead of /map
 * raw-spreading it, which silently broke once fields stopped being uniformly
 * array-valued (range fields decompose to scalar `_min`/`_max` keys;
 * scalar-text fields unwrap to a plain string) — see map-catalog-filter-parity.
 *
 * Returns `Record<string, unknown>` rather than `MediaUnitFilters`: the two
 * real callers merge this into either `MediaUnitFilters` (catalog) or
 * `MapPinsQuery` (map), which disagree on `status`'s element type, so a
 * caller narrows explicitly at its own call site instead of this function
 * committing to one contract type.
 */
// eslint-disable-next-line complexity -- three field-id loops + range decomposition, inherited from the pre-extraction panelValueToFilters
export function buildFilterDimensionsFromPanelValue(
  value: Record<string, string[]>,
): Record<string, unknown> {
  const filters: MediaUnitFilters = {};
  for (const key of FILTER_FIELD_IDS) {
    if (value[key]?.length) (filters as Record<string, string[]>)[key] = value[key]!;
  }
  // ─── New catalog-only filters (CU-86cataqt9) ───
  // material/multiformat/tags are array-valued; address/notes/roadNumber are
  // unwrapped to a scalar below.
  for (const key of CATALOG_ONLY_REAL_ARRAY_FIELD_IDS) {
    if (value[key]?.length) (filters as Record<string, string[]>)[key] = value[key]!;
  }
  for (const key of CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS) {
    const trimmed = value[key]?.[0]?.trim();
    if (trimmed) (filters as Record<string, string>)[key] = trimmed;
  }
  applyRangeFilters(filters, value);
  return filters as unknown as Record<string, unknown>;
}

// eslint-disable-next-line complexity -- many independent optional near-POI/sidecar scalars, each forwarded by its own guard
export function panelValueToFilters(
  value: Record<string, string[]>,
  nearPoi?: { nearKey?: string; nearValue?: string; nearRadius?: number | string },
  sidecar?: SidecarFilterParams,
): MediaUnitFilters {
  const filters = buildFilterDimensionsFromPanelValue(value) as unknown as MediaUnitFilters;
  if (nearPoi?.nearKey) filters.near_poi_key = nearPoi.nearKey;
  if (nearPoi?.nearValue) filters.near_poi_value = nearPoi.nearValue;
  if (nearPoi?.nearRadius) filters.near_poi_radius_m = Number(nearPoi.nearRadius);
  // ─── Sidecar scalar/array params (near-POI-style) ───
  const trimmedSearch = sidecar?.search?.trim();
  if (trimmedSearch) filters.search = trimmedSearch;
  if (sidecar?.noPhoto) filters.noPhoto = true;
  // Availability bounds are both-or-neither (the API ignores a single bound).
  if (sidecar?.availabilityFrom && sidecar.availabilityTo) {
    filters.availabilityFrom = sidecar.availabilityFrom;
    filters.availabilityTo = sidecar.availabilityTo;
  }
  if (sidecar?.exclude?.length) filters.exclude = sidecar.exclude;
  if (sidecar?.importJobId) filters.importJobId = sidecar.importJobId;
  return filters;
}
